import { z } from 'zod'

import { rtdbRecordSchema } from '../../services/rtdb-record'

export const validTimestampSchema = z
  .number()
  .finite()
  .positive()
  .max(8_640_000_000_000_000)

const optionalValue = z.unknown().optional()

export const patientInfoSchema = z
  .object({
    name: optionalValue,
    associatedPatientNames: z.array(z.string().max(321)).max(20).optional(),
    firstName: optionalValue,
    lastName: optionalValue,
    phoneNumber: optionalValue,
    birthday: optionalValue,
    dob: optionalValue,
    dateOfBirth: optionalValue,
    email: optionalValue,
    patientInsuranceInfo: z.unknown().optional(),
    procedure: optionalValue,
    patientStatus: optionalValue,
    source: optionalValue,
    AIMode: optionalValue,
    favorite: optionalValue,
    isEmergency: optionalValue,
    apptBooked: optionalValue,
    apptReq: optionalValue,
    callReq: optionalValue,
    cancelReq: optionalValue,
    resReq: optionalValue,
  })
  .passthrough()

export const lastMessageSchema = z
  .object({
    content: optionalValue,
    service: optionalValue,
    source: optionalValue,
    callId: optionalValue,
    threadID: optionalValue,
    timestamp: validTimestampSchema,
  })
  .passthrough()

export const patientSchema = z
  .object({
    info: patientInfoSchema.optional(),
    lastMessage: lastMessageSchema,
    trash: optionalValue,
    archive: optionalValue,
  })
  .passthrough()

export const threadSchema = z
  .object({
    startChatTs: validTimestampSchema,
    service: optionalValue,
    source: optionalValue,
  })
  .passthrough()

export const messageSchema = z
  .object({
    content: z.string(),
    timestamp: validTimestampSchema,
    receive: optionalValue,
    sender: optionalValue,
    service: optionalValue,
    source: optionalValue,
    status: optionalValue,
    deliveryState: optionalValue,
  })
  .passthrough()

export const callSchema = z
  .object({
    threadID: optionalValue,
    threadOwnership: z
      .object({
        method: z.literal('exact-call-id'),
        version: z.literal(1),
        threadID: optionalValue,
      })
      .optional(),
  })
  .passthrough()

const patientAssociationsSchema = z
  .object({
    status: z.enum([
      'MATCHED',
      'PARTIAL',
      'OVERFLOW',
      'UNRESOLVED',
      'UNAVAILABLE',
    ]),
    patients: z
      .array(
        z
          .object({
            patientId: z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/),
            displayName: z.string().min(1).max(321),
            firstName: z.string().max(160).nullable(),
            lastName: z.string().max(160).nullable(),
          })
          .strict()
      )
      .max(20),
  })
  .strict()

// The primary intent of the contact's latest customer interaction (a call or a
// Native Text conversation), projected by the Interaction Aggregate owner.
export const interactionIntentSchema = z.enum([
  'APPOINTMENT_BOOKED',
  'CANCELLATION_REQUEST',
  'RESCHEDULE_REQUEST',
  'APPOINTMENT_REQUEST',
  'CALLBACK_REQUEST',
  'MESSAGE_LEFT',
  'FRONT_DESK_REQUEST',
  'APPOINTMENT_QUESTION',
  'GENERAL_INQUIRY',
])

export type InteractionIntent = z.infer<typeof interactionIntentSchema>

// What became of a handoff (interaction_handoff).
export const handoffSummarySchema = z
  .object({
    state: z
      .enum([
        'REQUESTED',
        'IN_PROGRESS',
        'ANSWERED',
        'TRANSFERRED',
        'RETURNED',
        'NOT_ANSWERED',
        'CALLER_LEFT',
        'NOT_CONNECTED',
        'UNAVAILABLE',
        'UNVERIFIED',
      ])
      .optional(),
    reason: z
      .enum([
        'BREAK',
        'CLOSED',
        'DISABLED',
        'HOURS_UNVERIFIED',
        'NOT_CONFIRMED',
        'NOT_ANSWERED',
        'OTHER',
        'NO_ANSWER',
        'BUSY',
        'VOICEMAIL',
        'FAILED',
        'CANCELLED',
      ])
      .optional(),
    callback: z
      .enum(['SAVED', 'NOT_SAVED', 'PENDING', 'DECLINED', 'NOT_CONFIRMED'])
      .optional(),
  })
  .strict()

export type HandoffSummary = z.infer<typeof handoffSummarySchema>

// Every other request the interaction established, and the clinic topics it
// asked about: both only ever add to the primary intent.
export const informationResultSchema = z.enum(['ANSWERED', 'NOT_AVAILABLE'])
export const callerNeedSchema = z.enum([
  'RESOLVED',
  'UNRESOLVED',
  'UNCONFIRMED',
])

/** What the owners' records say of one interaction, beside its primary intent. */
export type ConversationFacts = {
  intent?: InteractionIntent | null
  secondary?: InteractionIntent[]
  handoff?: HandoffSummary | null
  information?: z.infer<typeof informationResultSchema>
  requestPassed?: boolean
  need?: z.infer<typeof callerNeedSchema>
  topics?: string[]
}

const interactionContextFields = {
  secondary: z.array(interactionIntentSchema).max(9).optional(),
  handoff: handoffSummarySchema.optional(),
  information: informationResultSchema.optional(),
  requestPassed: z.literal(true).optional(),
  need: callerNeedSchema.optional(),
  topics: z
    .array(z.string().regex(/^[A-Z_]{1,40}$/))
    .max(8)
    .optional(),
}

// The owner's merge order within one interaction (fixed-width evidence
// progress): a call's outcome revision, an SMS thread's evidence revision.
const interactionProgressPatterns = {
  VOICE: /^\d{16}\.\d{16}$/,
  SMS: /^\d{16}$/,
} as const

export const interactionAggregateSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    associations: patientAssociationsSchema.optional(),
    display: z
      .object({
        displayName: z.string().min(1).max(500),
        phoneDisplay: z.string().max(500).nullable(),
        latestActivityAtMs: validTimestampSchema,
        latestPreview: z.string().min(1).max(500),
        latestChannel: z.enum(['SMS', 'VOICE', 'WEB_WIDGET', 'UNKNOWN']),
      })
      .strict(),
    channels: z
      .object({
        hasSms: z.boolean(),
        hasVoice: z.boolean(),
        hasWidget: z.boolean(),
      })
      .strict(),
    visibility: z.enum(['VISIBLE', 'HIDDEN']),
    identity: z
      .object({
        status: z.enum([
          'LEGACY_BOUND',
          'CONFLICTED',
          'RECONCILIATION_REQUIRED',
        ]),
        pmsLinkageStatus: z.literal('NOT_EVALUATED'),
      })
      .strict(),
    latestInteraction: z
      .object({
        channel: z.enum(['VOICE', 'SMS']),
        ref: z.string().regex(/^[0-9a-f]{64}$/),
        atMs: validTimestampSchema,
        progress: z.string(),
        intent: interactionIntentSchema.nullable(),
        ...interactionContextFields,
      })
      .strict()
      .refine(
        (value) =>
          interactionProgressPatterns[value.channel].test(value.progress),
        { path: ['progress'] }
      )
      .optional(),
    // Stored beside the row. user-management merges it into latestInteraction; only an
    // older build, after a rollback, would pass it on. Ignored either way.
    latestInteractionFacts: z.unknown().optional(),
  })
  .strict()

// The inbox reads aggregates from RTDB, which omits null fields such as the
// absent phone of a calls-only caller.
export const interactionAggregateRecordSchema = rtdbRecordSchema(
  interactionAggregateSchema
)

export const interactionAggregateReadinessSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum(['READY', 'NOT_READY']),
  })
  .strict()

export const interactionAggregateLegacyRouteSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    legacyPatientId: z.string().min(1).max(512),
    lastMessageThreadId: z.string().min(1).max(512).nullable(),
    lastMessageCallId: z.string().min(1).max(512).nullable(),
  })
  .strict()

export const interactionPatientContextSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    recordKind: z.literal('FLIO_CONTACT'),
    associations: patientAssociationsSchema.optional(),
    displayName: z.string().trim().min(1).max(500),
    phone: z.string().trim().max(32).nullable(),
    email: z.string().trim().max(254).nullable(),
    engagementStatus: z.string().trim().max(80).nullable(),
    providerLinkStatus: z.enum([
      'NONE',
      'NEXHEALTH_LINK_RECORDED',
      'CONFLICTED',
    ]),
    pmsLinkageStatus: z.literal('NOT_VERIFIED'),
  })
  .strict()

export const interactionAppointmentContextSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    evidenceStatus: z
      .enum(['CURRENT', 'STALE', 'UNAVAILABLE', 'OVERFLOW'])
      .optional(),
    // Whether the PMS search ran (optional: older user-management builds).
    pmsLookup: z.enum(['COMPLETE', 'NOT_LINKED', 'FAILED']).optional(),
    windowEndBeforeMs: validTimestampSchema.nullable().optional(),
    appointments: z
      .array(
        z
          .object({
            patientId: z
              .string()
              .regex(/^nhrlink_v1_[a-f0-9]{64}$/)
              .nullable(),
            patientDisplayName: z.string().min(1).max(321),
            scheduledAtMs: validTimestampSchema,
            clinicTimeZone: z.string().min(1).max(80),
            status: z.enum(['SCHEDULED', 'CONFIRMED']),
            appointmentTypeName: z.string().max(160).nullable(),
            providerName: z.string().max(160).nullable(),
            operatoryName: z.string().max(160).nullable(),
            evidenceStatus: z.enum(['CURRENT', 'STALE', 'UNAVAILABLE']),
            observedAtMs: validTimestampSchema.nullable(),
            // When Flio last saw it in the PMS, and whether a later complete
            // read of its day no longer lists it (optional, as above).
            pmsObservedAtMs: validTimestampSchema.nullable().optional(),
            pmsConflict: z.boolean().optional(),
          })
          .strict()
      )
      .max(2000)
      .optional(),
    appointment: z
      .object({
        scheduledAtMs: validTimestampSchema,
        clinicTimeZone: z.string().trim().min(1).max(80),
        status: z.enum(['SCHEDULED', 'CONFIRMED']),
        appointmentTypeName: z.string().trim().max(160).nullable(),
        providerName: z.string().trim().max(160).nullable(),
        operatoryName: z.string().trim().max(160).nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict()

export const interactionInsuranceContextSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    insurance: z.null(),
    provenance: z.literal('UNAVAILABLE'),
  })
  .strict()

const interactionSummaryRequestTypeSchema = z.enum([
  'APPOINTMENT_REQUEST',
  'CANCELLATION_REQUEST',
  'RESCHEDULE_REQUEST',
  'CALLBACK_REQUEST',
])

export const interactionSummarySchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    freshness: z
      .object({
        status: z.enum([
          'CURRENT',
          'STALE',
          'GENERATING',
          'UNAVAILABLE',
          'FAILED',
        ]),
        aggregateActivityAtMs: validTimestampSchema,
        sourceActivityAtMs: validTimestampSchema.nullable(),
      })
      .strict(),
    advisory: z
      .object({
        intent: z.string().trim().min(1).max(400).nullable(),
        discussion: z.string().trim().min(1).max(1200).nullable(),
        outcome: z.null(),
        sourceChannels: z.array(z.enum(['SMS', 'VOICE'])).max(2),
        participantTypes: z.array(z.enum(['AI', 'PATIENT', 'STAFF'])).max(3),
        sourceCount: z.number().int().min(0).max(4),
        truncated: z.boolean(),
        // Each interaction's own advisory summary, newest first; ``current``
        // marks the one covering the latest meaningful communication (optional:
        // older user-management builds send only the joined discussion).
        entries: z
          .array(
            z
              .object({
                channel: z.enum(['SMS', 'VOICE']),
                atMs: validTimestampSchema,
                text: z.string().trim().min(1).max(1000),
                current: z.boolean(),
              })
              .strict()
          )
          .max(4)
          .optional(),
      })
      .strict(),
    businessTruth: z
      .object({
        appointmentStatus: z.enum(['SCHEDULED', 'CONFIRMED']).nullable(),
        unresolvedActionRequestCount: z.number().int().min(0).max(100),
        unresolvedActionRequestTypes: z
          .array(interactionSummaryRequestTypeSchema)
          .max(4),
        unresolvedActionRequestLabels: z
          .array(z.string().trim().min(1).max(80))
          .max(4),
        // The conversation's latest customer interaction and its one canonical
        // intent: the record the conversation row shows (optional, as above).
        latestInteraction: z
          .object({
            intent: interactionIntentSchema,
            channel: z.enum(['SMS', 'VOICE']),
            atMs: validTimestampSchema,
            ...interactionContextFields,
          })
          .strict()
          .nullable()
          .optional(),
      })
      .strict(),
  })
  .strict()

export const interactionInternalNoteSchema = z
  .object({
    noteRef: z.string().regex(/^internal_note_v1_[0-9a-f]{64}$/),
    body: z.string().min(1).max(2000),
    createdAtMs: validTimestampSchema,
    updatedAtMs: validTimestampSchema,
    revision: z.number().int().positive(),
    authorLabel: z.literal('Staff note'),
    editable: z.boolean(),
  })
  .strict()

export const interactionInternalNotesSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    notes: z.array(interactionInternalNoteSchema).max(50),
    nextCursor: z.string().min(1).max(768).nullable(),
  })
  .strict()

export const interactionInternalNoteMutationSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    note: interactionInternalNoteSchema,
    replayClassification: z.enum(['NEW', 'EXACT_REPLAY']),
  })
  .strict()

export const interactionInternalNoteDeleteSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    noteRef: z.string().regex(/^internal_note_v1_[0-9a-f]{64}$/),
    deleted: z.literal(true),
    revision: z.number().int().positive(),
    replayClassification: z.enum(['NEW', 'EXACT_REPLAY']),
  })
  .strict()

export type RawPatient = z.infer<typeof patientSchema>
export type RawThread = z.infer<typeof threadSchema>
export type RawMessage = z.infer<typeof messageSchema>
